import { Injectable, inject } from '@angular/core';
import { plainToInstance } from 'class-transformer';
import { BehaviorSubject, Subscription } from 'rxjs';
import { calendarTrackerStatusInterval } from '../../../../features/calendar/calendar.config';
import { CustomError } from '../../../../shared/data-types/client-error';
import {
  TimeBlockAbsenceModel,
  TimeBlockModel,
  TimeBlockProjectModel,
} from '../../../models/timeblock/time-block.model';
import { TrackerResponseModel } from '../../../models/tracker/tracker-response.model';
import { CronJobsService } from '../../cron-jobs.service';
import { WebSocketService } from '../../web-socket.service';

@Injectable({
  providedIn: 'root',
})
export class SharedTrackerHandlerService {
  public trackerResponse$ = new BehaviorSubject<null | TrackerResponseModel>(null);
  public trackerTimeBlockComponent$ = new BehaviorSubject<TimeBlockModel | null>(null);
  public trackerStatus$ = new BehaviorSubject<TrackerStatus>(TrackerStatus.None);

  // DI
  private readonly webSocketService = inject(WebSocketService);
  private readonly calendarCronJobsService = inject(CronJobsService);

  // Misc
  private webSocketSub$: Subscription | null = null;

  public registerTimeTracking(): void {
    // Subscription already exists, so return.
    if (this.webSocketSub$) {
      return;
    }

    this.webSocketSub$ = this.webSocketService.webSocket$.subscribe({
      next: (trackerResponse) => this.setResponse(trackerResponse), // Called whenever there is a message from the server.
      error: (err) => {
        throw new CustomError('Error', err);
      }, // Called if at any point WebSocket API signals some kind of error.
      complete: () => {
        throw new CustomError('Error', 'The connection closed unexpectedly.');
      }, // Called when connection is closed (for whatever reason).
    });

    // This job will run forever (not forever but as long as the app lives).
    this.calendarCronJobsService.registerJob(
      this.webSocketService.sendMessage,
      calendarTrackerStatusInterval,
    );
  }

  public deregisterTimeTracking(): void {
    this.trackerTimeBlockComponent$.next(null);
    this.trackerStatus$.next(TrackerStatus.None);
  }

  public timeBlockSettingsChanged(
    trackerTimeBlockModel: TimeBlockModel,
    originalTimeBlockModel: TimeBlockModel,
  ): boolean {
    if (
      trackerTimeBlockModel === null ||
      trackerTimeBlockModel === undefined ||
      originalTimeBlockModel === null ||
      originalTimeBlockModel === undefined
    ) {
      return false;
    }

    // Task changed.
    if (
      trackerTimeBlockModel instanceof TimeBlockProjectModel &&
      originalTimeBlockModel instanceof TimeBlockProjectModel
    ) {
      return trackerTimeBlockModel.task.id !== originalTimeBlockModel.task.id;
    }

    // Absence reason changed.
    if (
      trackerTimeBlockModel instanceof TimeBlockAbsenceModel &&
      originalTimeBlockModel instanceof TimeBlockAbsenceModel
    ) {
      return trackerTimeBlockModel.reason.id !== originalTimeBlockModel.reason.id;
    }

    return false;
  }

  private setResponse(trackerResponse: unknown): void {
    const trackerResponseModel = plainToInstance(TrackerResponseModel, trackerResponse);

    if (trackerResponseModel.isTrackerAlive && trackerResponseModel.timeBlockModel) {
      // Recording
      this.trackerStatus$.next(TrackerStatus.Recording);
    } else if (trackerResponseModel.isTrackerAlive && !trackerResponseModel.timeBlockModel) {
      // Pause
      this.trackerStatus$.next(TrackerStatus.Pause);
    } else {
      // None
      this.trackerStatus$.next(TrackerStatus.None);
    }

    this.trackerResponse$.next(trackerResponseModel);
  }
}

export enum TrackerStatus {
  Recording,
  Pause,
  None,
}
